import type { ResolveMap, ResolveMapEntry } from '@ms-cloudpack/common-types';
import { environmentInfo } from '@ms-cloudpack/environment';
import { createResolveMap, type CreateResolveMapOptions } from '@ms-cloudpack/package-utilities';
import { bold, bulletedList, cyan, plural, yellow } from '@ms-cloudpack/task-reporter';
import type { Context } from '../types/Context.js';

/**
 * Create the resolve map, logging the results to the task reporter.
 * Throws if dependencies could not be resolved.
 *
 * Usually this will contain all dependencies of `appPath` (and other paths if specified),
 * but if `onlyResolveDependencies` are set, it will only contain the package at `appPath`.
 */
export async function createResolveMapTask(
  options: Pick<
    CreateResolveMapOptions,
    | 'appPath'
    | 'additionalPaths'
    | 'linkedPaths'
    | 'skipResolveDependencies'
    | 'onlyResolveDependencies'
    | 'moduleResolutionConfig'
  >,
  context: Pick<Context, 'reporter' | 'packages' | 'telemetryClient'>,
): Promise<ResolveMap> {
  const {
    appPath,
    additionalPaths,
    linkedPaths,
    skipResolveDependencies,
    onlyResolveDependencies,
    moduleResolutionConfig,
  } = options;
  const { reporter, packages, telemetryClient } = context;
  let resolveMap: ResolveMap | undefined;

  const task = reporter.addTask(
    skipResolveDependencies || (onlyResolveDependencies && !onlyResolveDependencies.length)
      ? 'Creating resolve map'
      : onlyResolveDependencies?.length
        ? 'Resolving only inlined dependencies'
        : 'Resolving dependencies',
  );

  try {
    resolveMap = await telemetryClient.tracer.startActiveSpan('CREATE_RESOLVE_MAP', async (span) => {
      span.setAttributes({ linkedPaths: linkedPaths?.length || 0, skipResolveDependencies, onlyResolveDependencies });

      // await is required here to report telemetry correctly
      return await createResolveMap(
        {
          appPath,
          additionalPaths,
          linkedPaths,
          skipResolveDependencies,
          onlyResolveDependencies,
          moduleResolutionConfig,
        },
        { packages },
      );
    });

    let message: string;
    let extended: string | undefined;

    if (onlyResolveDependencies || skipResolveDependencies) {
      const included = ['the current package'];
      if (onlyResolveDependencies?.length) {
        included.push('its inlined dependencies');
      }
      if (additionalPaths?.length) {
        included.push(plural(additionalPaths.length, 'additional path'));
      }
      message = 'Created resolve map with ';
      if (included.length === 1) {
        message += `${included[0]} only.`;
      } else if (included.length === 2) {
        message += `${included[0]} and ${included[1]}.`;
      } else {
        message += `${included.slice(0, -1).join(', ')}, and ${included.slice(-1)}.`;
      }
    } else {
      const { allPackages, duplicatedPackages } = getPackagesFromResolveMap(resolveMap);

      message = `Found ${cyan(allPackages.size)} total packages, ${
        duplicatedPackages.size ? `${yellow(duplicatedPackages.size)} with multiple versions.` : `no duplicates.`
      }`;
      extended = bulletedList(
        Array.from(
          duplicatedPackages.entries(),
          ([name, versions]) => `${bold(name)}: ${Array.from(versions).join(', ')}`,
        ),
      );
    }

    task.complete({ message, extended, forceShow: true });

    return resolveMap;
  } catch (err) {
    task.complete({
      status: 'fail',
      message: 'Failed to resolve dependencies',
      errors: [String(err)],
      forceShow: true,
      // Don't use the message as "details" because this will cause the message to be logged twice
      extended: (err as Error).stack,
    });

    if (err instanceof Error && !environmentInfo.isJest) {
      // Remove the call stack because we already logged it in the extended details of the task
      // (unless running in a test, because it prevents the error from being logged in an understandable way)
      err.stack = undefined;
    }

    throw err;
  }
}

/**
 * Given a resolveMap, returns a map of all packages and a map of packages with multiple versions.
 * This is used to log the results of the resolve task.
 */
function getPackagesFromResolveMap(resolveMap: ResolveMap): {
  allPackages: Map<string, ResolveMapEntry>;
  duplicatedPackages: Map<string, Set<string>>;
} {
  const allPackages = new Map<string, ResolveMapEntry>();
  const duplicatedPackages = new Map<string, Set<string>>();

  function addEntry(entry: ResolveMapEntry): void {
    const existingEntry = allPackages.get(entry.name);
    if (existingEntry) {
      let dupeSet = duplicatedPackages.get(entry.name);
      if (!dupeSet) {
        dupeSet = new Set([existingEntry.version]);
        duplicatedPackages.set(entry.name, dupeSet);
      }
      dupeSet.add(entry.version);
    } else {
      allPackages.set(entry.name, entry);
    }
  }

  for (const currentEntry of Object.values(resolveMap)) {
    addEntry(currentEntry);

    if (currentEntry.scopedVersions) {
      for (const scopedEntry of Object.values(currentEntry.scopedVersions)) {
        addEntry(scopedEntry);
      }
    }
  }

  return {
    allPackages,
    duplicatedPackages,
  };
}
